import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ExportButtons } from "@/lib/exportData";
import Pagination, { usePagination } from "@/components/admin/Pagination";

export default function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [q, setQ] = useState("");

    const load = async () => {
        const r = await api.get("/admin/products");
        setProducts((r.data || []).filter((product) => !product.demo_catalog && !product.legacy_store_content));
    };
    useEffect(() => {
        load();
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this product?")) return;
        await api.delete(`/admin/products/${id}`);
        toast.success("Deleted");
        load();
    };

    const filtered = useMemo(
        () =>
            products.filter(
                (p) =>
                    !q ||
                    p.title.toLowerCase().includes(q.toLowerCase()) ||
                    p.category.toLowerCase().includes(q.toLowerCase())
            ),
        [products, q]
    );

    const exportRows = useMemo(
        () =>
            filtered.map((p) => ({
                id: p.id,
                title: p.title,
                slug: p.slug,
                category: p.category,
                price_inr: p.price / 100,
                compare_price_inr: p.compare_price ? p.compare_price / 100 : "",
                total_stock: p.variants?.reduce((s, v) => s + Math.max(0, v.stock - (v.reserved || 0)), 0) || 0,
                is_published: p.is_published,
                is_new: p.is_new,
                featured: p.featured,
                is_event_package: !!p.is_event_package,
            })),
        [filtered]
    );

    const { page, pageSize, pageItems, setPage, setPageSize } = usePagination(filtered, 20);

    return (
        <div data-testid="admin-products-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="font-display text-4xl">Products & event packages</h1>
                    <p className="text-muted-foreground mt-1">Manage standard products and bookable event packages · {products.length} items</p>
                </div>
                <Link
                    to="/admin/products/new"
                    className="dw-btn-primary px-5 h-10 font-mono-ui inline-flex items-center gap-2"
                    data-testid="admin-add-product-button"
                >
                    <Plus className="h-4 w-4" /> Add product
                </Link>
            </div>

            <Card className="mt-6 rounded-[20px] border-[#E7DED2] bg-white/80">
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        <Input
                            placeholder="Search products and packages…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="rounded-full bg-[#FAF7F1] border-[#E7DED2] max-w-xs"
                            data-testid="admin-products-search"
                        />
                        <ExportButtons
                            rows={exportRows}
                            filename="xplosiv_event_packages"
                            columns={[
                                "id",
                                "title",
                                "slug",
                                "category",
                                "price_inr",
                                "compare_price_inr",
                                "total_stock",
                                "is_published",
                                "is_new",
                                "featured",
                                "is_event_package",
                            ]}
                            className="ml-auto"
                        />
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="admin-products-table">
                            <thead>
                                <tr className="text-left text-xs font-mono-ui text-muted-foreground border-b border-[#E7DED2]">
                                    <th className="py-3">Product</th>
                                    <th>Category / type</th>
                                    <th>Price</th>
                                    <th>Stock</th>
                                    <th>Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageItems.map((p) => {
                                    const totalStock = p.variants?.reduce((s, v) => s + Math.max(0, v.stock - (v.reserved || 0)), 0) || 0;
                                    return (
                                        <tr key={p.id} className="border-b border-[#F4EFE6]" data-testid={`admin-product-row-${p.slug}`}>
                                            <td className="py-3">
                                                <div className="flex items-center gap-3">
                                                    <img src={p.images?.[0]} alt="" className="h-12 w-12 rounded-xl object-cover" />
                                                    <div>
                                                        <p className="font-medium">{p.title}</p>
                                                        <p className="text-xs text-muted-foreground">{p.slug}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td><div className="flex flex-col items-start gap-1"><span>{p.category}</span><span className={`rounded-full px-2.5 py-1 text-[9px] font-semibold ${p.is_event_package ? "bg-[#e7c45f]/25 text-[#65501d]" : "bg-[#e9efeb] text-[#355d4b]"}`}>{p.is_event_package ? "Event package" : "Product"}</span></div></td>
                                            <td>{formatPrice(p.price)}</td>
                                            <td>{totalStock}</td>
                                            <td>
                                                <span
                                                    className={`text-xs font-mono-ui px-2 py-1 rounded-full ${
                                                        p.is_published ? "bg-[#2F7D5A]/15 text-[#2F7D5A]" : "bg-[#F4EFE6]"
                                                    }`}
                                                >
                                                    {p.is_published ? "Live" : "Draft"}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="flex gap-2 justify-end">
                                                    <Link
                                                        to={`/admin/products/${p.id}/edit`}
                                                        className="h-8 w-8 rounded-full bg-[#F4EFE6] flex items-center justify-center hover:bg-[#E7DED2]"
                                                        data-testid={`admin-edit-${p.slug}`}
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </Link>
                                                    <button
                                                        onClick={() => handleDelete(p.id)}
                                                        className="h-8 w-8 rounded-full bg-[#F4EFE6] flex items-center justify-center hover:bg-[#B42318] hover:text-white"
                                                        data-testid={`admin-delete-${p.slug}`}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        total={filtered.length}
                        onPage={setPage}
                        onPageSize={setPageSize}
                    />
                </CardContent>
            </Card>
        </div>
    );
}
